/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  TimestampsInterface,
  dateOnly,
  SortConfig,
  SortConfigInput,
} from '@/types/models'

interface DatableAndTimestampable {
  createdAt?: Date | null
  updatedAt?: Date | null

  // eslint-disable-next-line
  [key: string]: any
}

interface DateAndTimestampable {
  date: dateOnly
  createdAt?: Date | null
  updatedAt?: Date | null

  // eslint-disable-next-line
  [key: string]: any
}

type DateInput = string | number | Date

const isDate = (val: any) =>
  Object.prototype.toString.call(val) === '[object Date]'
const isNumber = (val: any) => typeof val === 'number' || val instanceof Number
const isString = (val: any) => typeof val === 'string' || val instanceof String

const sortAlphaDirection =
  <T>(field: keyof T, mult = 1, next?: (a: T, b: T) => number) =>
  (a: T, b: T) =>
    a[field] > b[field]
      ? 1 * mult
      : a[field] < b[field]
      ? -1 * mult
      : next
      ? next(a, b)
      : 0
export const sortAlpha = <T>(field: keyof T, next?: (a: T, b: T) => number) =>
  sortAlphaDirection(field, 1, next)
export const sortAlphaDesc = <T>(
  field: keyof T,
  next?: (a: T, b: T) => number
) => sortAlphaDirection(field, -1, next)

const sortNumberDirection =
  <T>(field: keyof T, mult = 1, next?: (a: T, b: T) => number) =>
  (a: T, b: T) =>
    a[field] > b[field]
      ? 1 * mult
      : a[field] < b[field]
      ? -1 * mult
      : next
      ? next(a, b)
      : 0
export const sortNumber = <T>(field: keyof T, next?: (a: T, b: T) => number) =>
  sortNumberDirection(field, 1, next)
export const sortNumberDesc = <T>(
  field: keyof T,
  next?: (a: T, b: T) => number
) => sortNumberDirection(field, -1, next)

const sortDateDirection =
  <T extends DatableAndTimestampable>(
    field: keyof T,
    mult = 1,
    next?: (a: T, b: T) => number
  ) =>
  (a: T, b: T) =>
    new Date(a[field]).getTime() > new Date(b[field]).getTime()
      ? 1 * mult
      : new Date(a[field]).getTime() < new Date(b[field]).getTime()
      ? -1 * mult
      : next
      ? next(a, b)
      : 0
export const sortDate = <T extends DatableAndTimestampable>(
  field: keyof T,
  next?: (a: T, b: T) => number
) => sortDateDirection(field, 1, next)
export const sortDateDesc = <T extends DatableAndTimestampable>(
  field: keyof T,
  next?: (a: T, b: T) => number
) => sortDateDirection(field, -1, next)

const sorterToSortConfig = <T>(sorter: SortConfigInput<T>): SortConfig<T> =>
  isString(sorter)
    ? ({ field: sorter, desc: false } as SortConfig<T>)
    : Array.isArray(sorter)
    ? ({
        field: sorter[0],
        desc:
          sorter[1] &&
          (sorter[1].toString().toLowerCase().trim() === 'asc' ||
            sorter[1] === -1 ||
            sorter[1] === true),
      } as SortConfig<T>)
    : (sorter as SortConfig<T>)
const sortThing = <T>(a: T, b: T, sorter: SortConfig<T>) =>
  isDate(a[sorter.field]) && isDate(b[sorter.field])
    ? sortDateDirection(sorter.field as any, sorter.desc ? -1 : 1)(
        a as any,
        b as any
      )
    : isNumber(a[sorter.field]) && isNumber(b[sorter.field])
    ? sortNumberDirection(sorter.field, sorter.desc ? -1 : 1)(a, b)
    : sortAlphaDirection(sorter.field, sorter.desc ? -1 : 1)(a, b)
// const normalizeSortConfigInput = <T>(input: SortConfigInput<T>) => input
// export const sortStuff = <T>(...sorters: Array<SortConfigInput<T>>) => (a: T, b: T) => (Array.isArray(sorters) ? sorters : [sorters]).reduce((val, sorter) => val === 0 ? sortThing(a, b, sorterToSortConfig(sorter)) : val, 0)
export const sortStuff =
  <T>(...sorters: Array<SortConfigInput<T>>) =>
  (a: T, b: T) =>
    sorters.reduce(
      (val, sorter) =>
        val === 0 ? sortThing(a, b, sorterToSortConfig(sorter)) : val,
      0
    )

export const sortByDate =
  <T extends DateAndTimestampable>(desc = false) =>
  (a: T, b: T) => {
    return (
      (a.date < b.date
        ? 1
        : a.date > b.date
        ? -1
        : (b.createdAt ? b.createdAt.getTime() : Date.now()) -
          (a.createdAt ? a.createdAt.getTime() : Date.now())) * (desc ? 1 : -1)
    )
  }

const sortItemsByCreatedAt = (
  a: TimestampsInterface,
  b: TimestampsInterface,
  desc = false
) =>
  ((a.createdAt
    ? a.createdAt.getTime()
    : a.updatedAt
    ? a.updatedAt.getTime()
    : Date.now()) -
    (b.createdAt
      ? b.createdAt.getTime()
      : b.updatedAt
      ? b.updatedAt.getTime()
      : Date.now())) *
  (desc ? -1 : 1)

const sortItemsByUpdatedAt = (
  a: TimestampsInterface,
  b: TimestampsInterface,
  desc = false
) =>
  ((a.updatedAt
    ? a.updatedAt.getTime()
    : a.createdAt
    ? a.createdAt.getTime()
    : Date.now()) -
    (b.updatedAt
      ? b.updatedAt.getTime()
      : b.createdAt
      ? b.createdAt.getTime()
      : Date.now())) *
  (desc ? -1 : 1)

export const sortItemsByTimestamps = <T>(
  a: T,
  b: T,
  updatedAtFirst = false,
  desc = false
) =>
  updatedAtFirst
    ? sortItemsByUpdatedAt(a as any, b as any, desc)
    : sortItemsByCreatedAt(a as any, b as any, desc)

export const sortItemsByTimestampsDesc = <T>(
  a: T,
  b: T,
  updatedAtFirst = false
) => sortItemsByTimestamps(a, b, updatedAtFirst, true)
